'use client';

import { EmblaCarousel } from '@/features/home/components/embla-carousel';
import ImageLoad from '../../components/image-load';
import { useTranslations } from 'next-intl';
import useFn from '@/hooks/use-fn';
import { useRouter } from '@/i18n/navigation';
import { menuTabUrl } from '@/const/page-url';
import { track } from '@/utils/track';
import { useSportsList } from '../../services/sports';
import { Spin } from '@/components/ui-feat/spin';
import { useMemo } from 'react';
import type { SportsRes } from '@/types/api/hall/sports';
import GameCardWithTracking from '../../components/game-card-with-tracking';
import { GAME_TYPE } from '../../const/game';

export default function SportsContainer() {
  const t = useTranslations('home');
  const router = useRouter();

  const { data: sportsData, isLoading } = useSportsList.useQueryHook({});

  const sportsList = useMemo(() => {
    if (!sportsData?.content) return [];
    return sportsData.content as SportsRes['content'];
  }, [sportsData]);

  const title = sportsData?.title || 'Sports';

  const handleViewAll = useFn(() => {
    router.push(menuTabUrl.sports + '?trackSource=sports');
    track('container_button_click', {
      current_page: 'Home',
      container_name: 'sports',
      button_name: 'View All'
    });
  });

  const handleItemClick = useFn((item: { type: number; value: number; }) => {
    // 跳转类型：1-运动，2-赛事
    const params = item.type === 1
      ? `?sport_id=${item.value}`
      : `?event_id=${item.value}`;
    router.push(`${menuTabUrl.sports}${params}&trackSource=sports`);
    track('container_button_click', {
      current_page: 'Home',
      container_name: 'sports',
      button_name: 'View All',
    });
  });

  if (isLoading) {
    return (
      <div className="pl-[12px]">
        <div className="flex gap-2">
          <Spin className="h-[90px] w-[110px] rounded-[10px]" />
          <Spin className="h-[90px] w-[110px] rounded-[10px]" />
          <Spin className="h-[90px] w-[110px] rounded-[10px]" />
        </div>
      </div>
    );
  }

  if (!sportsList?.length) return null;

  return (
    <EmblaCarousel
      title={title}
      colors={['#FF613B', '#E8370D']}
      columns={3}
      rows={1}
      className="pl-[12px]"
      showViewAll={false}
      rightTitle={t('viewAll')}
      onClickViewAll={handleViewAll}
    >
      {sportsList?.map((item, index) => (
        <GameCardWithTracking
          currentPage="Home"
          currentModule="sports"
          key={index}
          item={{ id: item.value, game_type: GAME_TYPE.Sports }}
          index={index}
        >
          {({ cardRef, handleClick }) => (
            <div
              ref={cardRef}
              className='aspect-[110/90] active:scale-90 rounded-[10px] flex flex-col items-center justify-center overflow-hidden cursor-pointer'
              onClick={() => {
                handleClick();
                handleItemClick(item);
              }}
            >
              {item?.img ? (
                <ImageLoad
                  skeletonWidth={52}
                  skeletonHeight={29}
                  src={item.img}
                  alt=""
                  width={110}
                  height={90}
                  className='object-contain w-full h-full'
                />
              ) : (
                <Spin className='aspect-[110/90]' />
              )}
            </div>
          )}
        </GameCardWithTracking>
      ))}
    </EmblaCarousel>
  );
}
